import { useRef, useState, useEffect } from "react";
import useOnClickOutside from "../../../../../hooks/useOnClickOutside";
import rangy from "rangy";
import "rangy/lib/rangy-classapplier";
import "rangy/lib/rangy-highlighter";

const useHighlight = () => {
  const [color, setColor] = useState<
    "yellow" | "green" | "red" | "blue" | "transparent"
  >("yellow");
  const [isOpen, setIsOpen] = useState(false);
  const colorBoxContainerRef = useRef<HTMLDivElement>(null);
  const highlighterRef = useRef<any>(null);

  useEffect(() => {
    // Initialize rangy highlighter
    rangy.init();

    const highlighter = (rangy as any).createHighlighter();

    // Create class appliers for each color
    highlighter.addClassApplier((rangy as any).createClassApplier("highlight-yellow", {
      ignoreWhiteSpace: true,
      tagNames: ["span"]
    }));

    highlighter.addClassApplier((rangy as any).createClassApplier("highlight-green", {
      ignoreWhiteSpace: true,
      tagNames: ["span"]
    }));

    highlighter.addClassApplier((rangy as any).createClassApplier("highlight-red", {
      ignoreWhiteSpace: true,
      tagNames: ["span"]
    }));

    highlighter.addClassApplier((rangy as any).createClassApplier("highlight-blue", {
      ignoreWhiteSpace: true,
      tagNames: ["span"]
    }));

    highlighterRef.current = highlighter;
  }, []);

  useOnClickOutside(colorBoxContainerRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const savedRange = useRef<Range | null>(null);
  const savedFormState = useRef<{
    element: HTMLTextAreaElement | HTMLInputElement;
    start: number;
    end: number;
  } | null>(null);

  const saveSelection = () => {
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === "TEXTAREA" ||
        (activeEl.tagName === "INPUT" &&
          (activeEl as HTMLInputElement).type === "text"))
    ) {
      const el = activeEl as HTMLTextAreaElement | HTMLInputElement;
      savedFormState.current = {
        element: el,
        start: el.selectionStart || 0,
        end: el.selectionEnd || 0,
      };
      savedRange.current = null;
      return;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
      savedFormState.current = null;
    }
  };

  const highLightSelections = () => {
    if (!highlighterRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!range || !range.toString().trim()) return;

    // Check if we are in an allowed area
    const container = range.commonAncestorContainer;
    const allowedSelectors = [
      ".scratch-pad",
      ".word-processor",
      ".spreadsheet-view",
      "input",
      "textarea",
      '[contenteditable="true"]',
      ".page-content",
    ];

    let currentNode: Node | null = container;
    let isAllowed = false;

    while (currentNode && currentNode !== document.body) {
      if (currentNode.nodeType === 1) {
        const element = currentNode as Element;
        if (
          allowedSelectors.some(
            (selector) => element.matches && element.matches(selector)
          )
        ) {
          isAllowed = true;
          break;
        }
      }
      currentNode = currentNode.parentNode;
    }

    if (!isAllowed) return;

    const highlighter = highlighterRef.current;

    // If transparent, remove all highlights in selection
    if (color === "transparent") {
      highlighter.unhighlightSelection();
      selection.removeAllRanges();
      return;
    }

    // Apply the highlight with the current color
    highlighter.highlightSelection(`highlight-${color}`);

    // Clear selection
    selection.removeAllRanges();
    savedRange.current = null;
  };

  return {
    isOpen,
    setIsOpen,
    colorBoxContainerRef,
    color,
    setColor,
    highLightSelections,
    saveSelection,
  };
};

export default useHighlight;
